import { GroupResult, groupBy } from '@progress/kendo-data-query';
import { format, getYear } from 'date-fns';
import { isNull, sortBy, uniqBy } from 'lodash';
import uuid from 'react-uuid';
import { IOcrData, ocrInvoiceFields } from 'src/sections/invoice_details/OcrInvoice';
import { fiscalYearType } from 'src/sections/view_checkbook/ImportExport/Export';
import {
  PeriodLineItemError,
  PeriodLineItemInfo,
  PeriodLineItemWarning,
} from 'src/sections/view_periods/usePeriodErrors';
import { IGlSummary, IItemsActionRequired } from 'src/services/useActionRequiredInvoicesApi';
import { FiscalAllYearsResponse } from 'src/services/useAllGetFiscalYearsApi';
import { Department, DepartmentMembers } from 'src/services/useGetDepartmentApi';
import { OcrField, OcrInvoiceResponse, OcrItem } from 'src/services/useGetOcrResponseApi';
import { GLPeriods } from 'src/services/useGLPeriodApi';
import { IInvoiceListItem } from 'src/types/Invoice';
import { IInvoiceDocument } from 'src/types/InvoiceDocument';
import { IReceiptListItem } from 'src/types/Receipt';
import { ViewVariance } from 'src/types/ViewVariance';
import { round } from '.';
import { fToMMDDYY, getCurrentYear, removeTime } from './formatTime';

export const formatViewReceipts = (
  receipts: IReceiptListItem[],
  companyName: string,
  prev?: IReceiptListItem[]
) => {
  const previouslyPreviewId = prev?.find((receipt) => receipt.preview)?.receiptId;
  return (
    receipts.map((item) => ({
      ...item,
      rowId: uuid(),
      receiptStatusDate: item.receiptStatusDate
        ? format(new Date(item.receiptStatusDate), 'MM/dd/yyy')
        : '',
      poDate: item.poDate ? format(new Date(item.poDate), 'MM/dd/yyy') : '',
      receiptDate: item.receiptDate ? format(new Date(item.receiptDate), 'MM/dd/yyy') : '',
      receiptTTLQty: item.receiptTTLQty || 0,
      percentReceived: Number(
        (item.poQty !== 0 ? (item.receiptTTLQty / item.poQty) * 100 : 0).toFixed(2)
      ),
      companyName,
      preview: previouslyPreviewId === item.receiptId,
    })) || []
  );
};

export const formatViewInvoices = (invoices: IInvoiceListItem[], prev?: IInvoiceListItem[]) => {
  const previouslyPreviewId = prev?.find((receipt) => receipt.preview)?.receiptId;

  return (
    invoices.map((item) => ({
      ...item,
      rowId: uuid(),
      statusDate: item.statusDate ? format(new Date(item.statusDate), 'MM/dd/yyy') : '',
      poDate: item.poDate ? format(new Date(item.poDate), 'MM/dd/yyy') : '',
      invoiceNumber: item.invoiceNumber,
      percentRecvByDollar: !isNull(item.percentRecvByDollar)
        ? Number(
            ((item?.percentRecvByDollar !== 0 ? item?.percentRecvByDollar : 0) as Number)?.toFixed(
              2
            )
          )
        : 'N/A',
      percentRecvByQuantity: !isNull(item?.percentRecvByQuantity)
        ? Number(
            (
              (item?.percentRecvByQuantity !== 0 ? item?.percentRecvByQuantity : 0) as Number
            )?.toFixed(2)
          )
        : 'N/A',
      preview: previouslyPreviewId === item.receiptId,
    })) || []
  );
};

export const sortGLPeriods = (arr: GLPeriods) =>
  arr.sort((a, b) => {
    if (a.fiscalYear !== b.fiscalYear) {
      return b.fiscalYear - a.fiscalYear;
    } else {
      return Number(new Date(b.startDate)) - Number(new Date(a.startDate));
    }
  });

const formatGLSummary = (
  glSummaries?: IGlSummary[],
  budgetPeriodStartDate?: string,
  budgetPeriodEndDate?: string
) =>
  glSummaries?.map((glSummary) => ({
    ...glSummary,
    budgetPeriod: `${
      budgetPeriodStartDate ? format(new Date(budgetPeriodStartDate), 'MM/dd/yyy') : ''
    } - ${budgetPeriodEndDate ? format(new Date(budgetPeriodEndDate), 'MM/dd/yyy') : ''}`,
  })) || [];

export const formatActionRequired = (invoices?: IItemsActionRequired[]) =>
  invoices?.map((invoice) => ({
    ...invoice,
    invoiceNumber: invoice?.invoiceNumber ? invoice?.invoiceNumber : 'Unassigned',
    statusDate: invoice?.statusDate ? format(new Date(invoice?.statusDate), 'MM/dd/yyy') : '',
    hasGlMismatchItems: invoice.hasGlMismatchItems ? 'Y' : ('N' as 'Y' | 'N'),
    poItemsCompleted: invoice.poItemsCompleted ? 'Y' : ('N' as 'Y' | 'N'),
    percentRecvByDollar: !isNull(invoice.percentRecvByDollar)
      ? Number(
          (
            (invoice.percentRecvByDollar !== 0 ? invoice.percentRecvByDollar : 0) as Number
          )?.toFixed(2)
        )
      : 'N/A',
    percentRecvByQuantity: !isNull(invoice.percentRecvByQuantity)
      ? Number(
          (
            (invoice.percentRecvByQuantity !== 0 ? invoice.percentRecvByQuantity : 0) as Number
          )?.toFixed(2)
        )
      : 'N/A',
    glSummaries: formatGLSummary(
      invoice?.glSummaries,
      invoice?.budgetPeriodStartDate,
      invoice?.budgetPeriodEndDate
    ),
  })) || [];

export const formatEnterReceipts = (data: any[], field: string) => {
  if (data.length === 1) {
    return data.map((item) => ({
      ...item,
      [field]: convertDate(item[field]),
      rowId: uuid(),
      selected: true,
    }));
  } else
    return (
      data.map((item) => ({
        ...item,
        [field]: convertDate(item[field]),
        rowId: uuid(),
        selected: false,
      })) || []
    );
};

export const formatEnterInvoices = (data: IInvoiceDocument[], subString: string) => {
  if (data.length === 1) {
    return data.map((item) => ({
      ...item,
      statusDate: convertDate(item.statusDate),
      selected: String(item.invoiceNumber).includes(subString),
    }));
  } else
    return (
      data.map((item) => ({
        ...item,
        statusDate: convertDate(item.statusDate),
      })) || []
    );
};

export const formatVariances = (variances: ViewVariance[]) =>
  variances.map((item) => ({
    ...item,
    poDate: format(new Date(item.poDate), 'MM/dd/yyy'),
    varianceQuantityPercentage: `${round(item.varianceQuantityPercentage)}%`,
    varianceDollar: `$${round(item.varianceDollar)}`,
    varianceDollarPercentage: `${round(item.varianceDollarPercentage)}%`,
  }));

export interface GLPeriodDropdownChildren {
  period: string;
  startDate: string;
  endDate: string;
  glPeriodId: number;
  content: string;
  label: string;
  id: number;
  fiscalYear: number;
  fiscalYearDescription: string;
  expanded?: boolean;
  selected?: boolean;
  year: number;
  hasTransactionData: boolean;
  isClosed?: number;
}
export interface GLPeriodDropdown {
  fiscalYear: number;
  year: number;
  items: GLPeriodDropdownChildren[];
  content: string;
  id: number;
  parent: boolean;
  yearStartDate: Date;
  yearEndDate: Date;
  isClosed?: number;
}

export interface GLPeriodYearDropdown {
  fiscalYear: number;
  year: number;
  id: number;
  content: string;
  parent: boolean;
  yearStartDate: Date;
  yearEndDate: Date;
  periods: GLPeriods;
  isClosed?: number;
}

export interface GLPeriodCalendarYearChildren {
  glPeriodId: number | null;
  startDate: Date | null;
  endDate: Date | null;
  hasTransactionData: boolean;
  rowId: string;
  isClosed: number | null;
  errors?: PeriodLineItemError;
  warnings?: PeriodLineItemWarning;
  infos?: PeriodLineItemInfo;
  isNewPeriod?: boolean;
  canClosePeriod: boolean;
}

export interface GLPeriodCalendarYear {
  selected?: boolean;
  fiscalYear: number;
  fiscalYearContent: string;
  year: number;
  yearStartDate: string;
  yearEndDate: string;
  hasTransactionData: boolean;
  isCurrYear: boolean;
  periods: GLPeriodCalendarYearChildren[];
  noOFPeriods: number;
  yearStartDateObj: Date;
  yearEndDateObj: Date;
  isPast: boolean;
}

const isPastOrCurrent = (startDate: string) => new Date() >= removeTime(new Date(startDate));
const isPeriodClosed = (isClosed: number | null) => isClosed === 1;

const formatGLPeriod = (glPeriods: GLPeriods) => {
  glPeriods = glPeriods.sort(
    (a, b) => Number(new Date(a.startDate)) - Number(new Date(b.startDate))
  );

  glPeriods = glPeriods.map((period, index) => ({
    ...period,
    canClosePeriod:
      isPeriodClosed(glPeriods?.[index - 1]?.isClosed) &&
      !isPeriodClosed(period?.isClosed) &&
      isPastOrCurrent(period.startDate),
  }));

  const groupByFiscalYear = groupBy(glPeriods, [
    { field: 'fiscalYear', dir: 'desc' },
  ]) as GroupResult[];

  const formattedGlPeriods = groupByFiscalYear.map((item) => {
    const period: GLPeriods = (item.items as GLPeriods)
      .sort((a, b) => Number(new Date(a.startDate)) - Number(new Date(b.startDate)))
      .map((item, index) => ({ ...item, period: String(index + 1) }));
    const yearStartDate = new Date(period[0].startDate);
    const yearEndDate = new Date(period[period.length - 1].endDate);
    return {
      fiscalYear: item.value as number,
      yearStartDate,
      yearEndDate,
      year: getYear(yearStartDate) as number,
      period,
    } as const;
  });
  return formattedGlPeriods.sort((a, b) => a.fiscalYear - b.fiscalYear);
};

export const formatGLPeriodDropDown = (glPeriods: GLPeriods): GLPeriodDropdown[] => {
  const transformGlPeriodData = formatGLPeriod(glPeriods);
  const formattedData: GLPeriodDropdown[] = transformGlPeriodData
    .map((item) => {
      const { fiscalYear, year, yearStartDate, yearEndDate, period } = item;
      const formattedYearStartDate = format(yearStartDate, 'MM/dd/yyyy');
      const formattedYearEndDate = format(yearEndDate, 'MM/dd/yyyy');
      const items: GLPeriodDropdownChildren[] = period.map((item) => ({
        ...item,
        isClosed: Number(item?.isClosed),
        id: item.glPeriodId,
        content: `Period ${item.period} (${fToMMDDYY(item.startDate)} - ${fToMMDDYY(
          item.endDate
        )})`,
        label: `${fToMMDDYY(item.startDate)} - ${fToMMDDYY(item.endDate)}`,
        fiscalYearDescription: `FY${fiscalYear} (${formattedYearStartDate} - ${formattedYearEndDate})`,
      }));
      return {
        fiscalYear,
        year,
        id: fiscalYear,
        content: `FY${fiscalYear} (${formattedYearStartDate} - ${formattedYearEndDate})`,
        parent: true,
        items,
        yearStartDate,
        yearEndDate,
      };
    })
    .sort((a, b) => b.fiscalYear - a.fiscalYear);

  return formattedData;
};
export const GLPeriodDropDownFormatByDate = (glPeriods: GLPeriods): GLPeriodDropdown[] => {
  const transformGlPeriodData = formatGLPeriod(glPeriods);
  const formattedData: GLPeriodDropdown[] = transformGlPeriodData
    .map((item) => {
      const { fiscalYear, year, yearStartDate, yearEndDate, period } = item;
      const formattedYearStartDate = format(yearStartDate, 'MM/dd/yyyy');
      const formattedYearEndDate = format(yearEndDate, 'MM/dd/yyyy');
      const items: GLPeriodDropdownChildren[] = period.map((item) => ({
        ...item,
        isClosed: Number(item?.isClosed),
        id: item.glPeriodId,
        content: `Period ${item.period} (${fToMMDDYY(item.startDate)} - ${fToMMDDYY(
          item.endDate
        )})`,
        label: `${fToMMDDYY(item.startDate)} - ${fToMMDDYY(item.endDate)}`,
        fiscalYearDescription: `FY${fiscalYear} (${formattedYearStartDate} - ${formattedYearEndDate})`,
      }));
      return {
        fiscalYear,
        year,
        id: fiscalYear,
        content: `${formattedYearStartDate} - ${formattedYearEndDate}`,
        parent: true,
        items,
        yearStartDate,
        yearEndDate,
      };
    })
    .sort((a, b) => b.fiscalYear - a.fiscalYear);

  return formattedData;
};

export const formatCalendarYearTable = (arr: GLPeriods): GLPeriodCalendarYear[] => {
  const currYear: number = getCurrentYear();
  const transformGlPeriodData = formatGLPeriod(arr);
  const formattedData: GLPeriodCalendarYear[] = transformGlPeriodData.map((item) => {
    const { fiscalYear, year, yearStartDate, yearEndDate, period } = item;
    const formattedYearStartDate = format(yearStartDate, 'MM/dd/yyyy') as string;
    const formattedYearEndDate = format(yearEndDate, 'MM/dd/yyyy') as string;
    let hasTransactionData = false;

    const periods: GLPeriodCalendarYearChildren[] = period.map((item, index) => {
      if (item.hasTransactionData) {
        hasTransactionData = true;
      }
      return {
        glPeriodId: item.glPeriodId,
        startDate: removeTime(new Date(item.startDate)),
        endDate: removeTime(new Date(item.endDate)),
        hasTransactionData: item.hasTransactionData,
        isClosed: item.isClosed,
        rowId: item.rowId,
        canClosePeriod: item.canClosePeriod,
      };
    });

    return {
      fiscalYear,
      year,
      id: fiscalYear,
      fiscalYearContent: `FY${fiscalYear}`,
      yearStartDate: formattedYearStartDate,
      yearEndDate: formattedYearEndDate,
      yearStartDateObj: removeTime(new Date(yearStartDate)),
      yearEndDateObj: removeTime(new Date(yearEndDate)),
      hasTransactionData,
      isCurrYear: currYear === year,
      isPast: currYear > year,
      noOFPeriods: period.length,
      periods,
    };
  });

  return formattedData;
};

export const formatGLYearDropDown = (arr: GLPeriods): GLPeriodYearDropdown[] => {
  const transformGlPeriodData = formatGLPeriod(arr);
  const formattedData = transformGlPeriodData
    .map((item) => {
      const { fiscalYear, year, yearStartDate, yearEndDate, period } = item;
      const formattedYearStartDate = format(yearStartDate, 'MM/dd/yyyy');
      const formattedYearEndDate = format(yearEndDate, 'MM/dd/yyyy');

      return {
        fiscalYear,
        year,
        id: fiscalYear,
        content: `FY${fiscalYear} (${formattedYearStartDate} - ${formattedYearEndDate})`,
        parent: true,
        yearStartDate,
        yearEndDate,
        periods: period,
      };
    })
    .sort((a, b) => b.fiscalYear - a.fiscalYear);

  return formattedData;
};

export const transformResponse = (arr: any[], identifier: string) => {
  let transformedResponse: any[] = [];
  switch (identifier) {
    case 'CREATE_PO':
      arr?.forEach((obj: any) => {
        const newObj = {
          ...obj,
          statusDate: convertDate(obj.statusDate),
        };
        transformedResponse.push(newObj);
      });
      return transformedResponse;
    case 'CREATE_RECEIPT':
      arr?.forEach((obj: any) => {
        const newObj = {
          ...obj,
          creationDate: convertDate(obj.creationDate),
        };
        transformedResponse.push(newObj);
      });
      return transformedResponse;
    case 'PO_DOCUMENT':
      arr?.forEach((obj: any) => {
        const newObj = {
          ...obj,
          rowId: uuid(),
          statusDate: convertDate(obj.statusDate),
        };
        transformedResponse.push(newObj);
      });
      return transformedResponse;

    case 'PO_HISTORY':
      arr?.forEach((obj: any) => {
        const newObj = {
          ...obj,
          actionDate: convertDate(obj.actionDate),
        };
        transformedResponse.push(newObj);
      });
      return transformedResponse;

    case 'LINK_INVOICES':
      arr?.forEach((obj: any) => {
        const {
          invoiceNumber,
          documentQty,
          totalCostAmt: invoiceTotal,
          lastUpdatedDate: statusDate,
          poTotal,
          receiptId,
          selected,
        } = obj;
        const newObj = {
          invoiceNumber,
          docType: invoiceTotal >= 0 || !invoiceTotal ? 'Invoice' : 'Credit Memo',
          documentQty,
          invoiceTotal: !invoiceTotal ? '$' + 0 : '$' + invoiceTotal,
          statusDate: format(convertDate(statusDate || new Date()), 'MM/dd/yyy'),
          poTotal: '$' + poTotal,
          receiptId,
          selected,
        };
        transformedResponse.push(newObj);
      });
      return transformedResponse;
  }
  return transformedResponse;
};

export function trimContentToGivenWidth(text: string, width: number) {
  if (text.length < width) return text;
  return text.slice(0, width) + '...';
}

export const convertDate = (date: string | Date) => new Date(date);

export const getUserCompanyDetails = (user: any) => {
  const companies = user?.Companies;
  if (!companies) return [];

  //Normal Buyer
  if (!Array.isArray(companies) && typeof companies === 'string') {
    const splitArr = companies.split(':');
    return [{ companyName: splitArr[1], companyId: splitArr[0] }];
  }

  return companies.map((company: string) => {
    const splitArr = company.split(':');
    return { companyName: splitArr[1], companyId: splitArr[0] };
  });
};

export function findCommonMembers(arrays: Department[]) {
  if (arrays.length === 0) return [];

  // Extract member arrays from the input arrays
  const membersArrays = arrays.map((obj) => obj.members);

  // Initialize with the members from the first array
  let commonMembers = [...membersArrays[0]];

  // Iterate through each member array
  for (let i = 1; i < membersArrays.length; i++) {
    const currentMembers = membersArrays[i];

    // Filter out members that are not present in the current array
    commonMembers = commonMembers.filter((member) =>
      currentMembers.some((m) => m.id === member.id)
    );
  }

  return commonMembers;
}
export function findCommonGLCodes(arrays: Department[]) {
  if (arrays.length === 0) return [];

  // Extract glcodes arrays from the input arrays
  const glCodeArrays = arrays.map((obj) => obj.glCodes);

  // Initialize with the glcodes from the first array
  let commonGLCodes = [...glCodeArrays[0]];

  // Iterate through each glcodes array
  for (let i = 1; i < glCodeArrays.length; i++) {
    const currentGLCodes = glCodeArrays[i];

    // Filter out glcodes that are not present in the current array
    commonGLCodes = commonGLCodes.filter((glCode) =>
      currentGLCodes.some((m) => m.id === glCode.id)
    );
  }

  return commonGLCodes;
}

export function mergeMembersArrays(arrays: Department[]) {
  let mergedArray: DepartmentMembers[] = [];
  arrays.forEach((array) => {
    array.members.forEach((member) => {
      const existingMember = mergedArray.find((m) => m.id === member.id);
      if (!existingMember) {
        mergedArray.push(member);
      }
    });
  });
  return mergedArray;
}

export function mergeGLCodesArrays(arrays: Department[]) {
  let mergedArray: DepartmentMembers[] = [];
  arrays.forEach((array) => {
    array.glCodes.forEach((glCode) => {
      const existingGLCode = mergedArray.find((g) => g.id === glCode.id);
      if (!existingGLCode) {
        mergedArray.push(glCode);
      }
    });
  });
  return mergedArray;
}
const formatDateContent = (date: string) => format(new Date(date), 'MM/dd/yyyy');

export const formatFiscalYears = (years: fiscalYearType[]) =>
  years?.map((yr) => ({
    content: `${formatDateContent(yr.startDate)}-${formatDateContent(yr.endDate)}`,
    startDate: yr.startDate,
    endDate: yr.endDate,
  }));

export const getUniqueProperties = (fiscalYears: FiscalAllYearsResponse[]) =>
  sortBy(uniqBy(fiscalYears, 'companyId'), 'companyName');
export function formatOcrResponse(data: OcrInvoiceResponse): OcrInvoiceResponse {
  const traverseAndUpdate = (obj: any) => {
    if (obj && typeof obj === 'object') {
      for (const key in obj) {
        if (key === 'boundingBox' && Array.isArray(obj[key])) {
          // Insert isHighlight field
          obj.isHighlight = false;
        }
        // Recursively traverse nested objects
        traverseAndUpdate(obj[key]);
      }
    }
  };

  const clonedData = Object.assign({}, data); // Clone the object to avoid mutation
  traverseAndUpdate(clonedData);
  return clonedData;
}

export function formatOcrData(data: any): IOcrData[] {
  let ocrData: IOcrData[] = [];
  for (const key in data) {
    const value = data[key];
    if (ocrInvoiceFields.includes(key) && value !== null && typeof value === 'object') {
      ocrData.push({ field: key, ...value });
    } else if (key === 'items' && Array.isArray(value)) {
      value.forEach((item) => {
        const ocrItems = Object.entries(item.item as OcrItem)
          .filter(([key, val]) => typeof val === 'object')
          .map(([key, val]) => ({
            field: key,
            ...(val as OcrField),
          }));
        ocrData.push(...ocrItems);
      });
    }
  }

  return ocrData;
}
